import {
  LUCK_PRINTER_NOTIFY_CHAR_UUID,
  LUCK_PRINTER_SERVICE_UUID,
  LUCK_PRINTER_WRITE_CHAR_UUID,
  WEB_BLE_CHUNK_DELAY_MS,
  WEB_BLE_CHUNK_SIZE,
  sleep,
} from "./protocol";
import {
  getSavedLuckPrinterDevice,
  saveLuckPrinterDevice,
} from "./storage";

export class LuckPrinterBleTransport {
  private device: BluetoothDevice | null = null;
  private writeChar: BluetoothRemoteGATTCharacteristic | null = null;
  private notifyChar: BluetoothRemoteGATTCharacteristic | null = null;
  private notifyHandler:
    | ((event: Event) => void)
    | null = null;
  private lastNotifyData: Uint8Array | null = null;

  get connectedDevice(): BluetoothDevice | null {
    return this.device;
  }

  async requestDevice(): Promise<BluetoothDevice> {
    if (!navigator.bluetooth) {
      throw new Error("Web Bluetooth is not supported");
    }

    return navigator.bluetooth.requestDevice({
      acceptAllDevices: true,
      optionalServices: [LUCK_PRINTER_SERVICE_UUID],
    });
  }

  async connect(options?: { device?: BluetoothDevice }): Promise<BluetoothDevice> {
    if (!navigator.bluetooth) {
      throw new Error("Web Bluetooth is not supported");
    }

    let device = options?.device ?? null;

    if (!device) {
      const saved = getSavedLuckPrinterDevice();
      if (saved && navigator.bluetooth.getDevices) {
        const devices = await navigator.bluetooth.getDevices();
        device = devices.find((entry) => entry.id === saved.id) ?? null;
      }
    }

    if (!device) {
      device = await this.requestDevice();
    }

    if (!device.gatt) {
      throw new Error("Bluetooth GATT is not available on this device");
    }

    const server = device.gatt.connected
      ? device.gatt
      : await device.gatt.connect();
    const service = await server.getPrimaryService(LUCK_PRINTER_SERVICE_UUID);
    this.writeChar = await service.getCharacteristic(LUCK_PRINTER_WRITE_CHAR_UUID);
    this.notifyChar = await service.getCharacteristic(
      LUCK_PRINTER_NOTIFY_CHAR_UUID,
    );

    this.lastNotifyData = null;
    await this.notifyChar.startNotifications();
    this.notifyHandler = (event) => {
      const target = event.target as BluetoothRemoteGATTCharacteristic;
      if (!target.value) return;
      this.lastNotifyData = new Uint8Array(target.value.buffer);
    };
    this.notifyChar.addEventListener(
      "characteristicvaluechanged",
      this.notifyHandler,
    );

    this.device = device;
    saveLuckPrinterDevice(device.id, device.name ?? "Printer");
    return device;
  }

  async write(data: Uint8Array): Promise<void> {
    if (!this.writeChar) throw new Error("Printer is not connected");
    await this.writeChar.writeValueWithoutResponse(data);
  }

  async writeChunks(
    data: Uint8Array,
    chunkSize = WEB_BLE_CHUNK_SIZE,
    delayMs = WEB_BLE_CHUNK_DELAY_MS,
  ): Promise<void> {
    for (let offset = 0; offset < data.length; offset += chunkSize) {
      const chunk = data.subarray(offset, offset + chunkSize);
      await this.write(chunk);
      if (delayMs > 0 && offset + chunkSize < data.length) {
        await sleep(delayMs);
      }
    }
  }

  async waitForStopAck(timeoutMs = 8000): Promise<void> {
    const started = Date.now();
    while (Date.now() - started < timeoutMs) {
      if (this.lastNotifyData) {
        if (this.lastNotifyData[0] === 0xaa) return;
        const text = new TextDecoder()
          .decode(this.lastNotifyData)
          .trim()
          .toUpperCase();
        if (text === "AA" || text === "OK") return;
      }
      await sleep(50);
    }
    throw new Error("Printer stop acknowledgement timeout");
  }

  async disconnect(): Promise<void> {
    if (this.notifyChar && this.notifyHandler) {
      this.notifyChar.removeEventListener(
        "characteristicvaluechanged",
        this.notifyHandler,
      );
      try {
        await this.notifyChar.stopNotifications();
      } catch {
        // ignore disconnect race
      }
    }

    if (this.device?.gatt?.connected) {
      this.device.gatt.disconnect();
    }

    this.device = null;
    this.writeChar = null;
    this.notifyChar = null;
    this.notifyHandler = null;
    this.lastNotifyData = null;
  }
}
